import { AnyAction } from 'redux';
import produce from 'immer';
import {
  FILTERS_ACTIONS,
  PRICE_RECOMMENDATION_MODAL_ACTIONS,
  PRICE_MATRICE_MODAL_ACTIONS,
  FLOORPLAN_ACTIONS,
  MODULE_ACTIONS,
  STATS_ACTIONS,
  UNITS_ACTIONS,
  FLAG_ITEM_MODAL_ACTIONS,
} from './../actions/leasePricingUnified';
import { UnifiedLeasePricingStateModel } from '../components/LeasePricing/UnifiedView/models';
import { OFFER_STATES } from '../constants';
import { ALL } from '../constants';
import { AUTH_ACTIONS } from '../actions/auth';
import { generateLeaseKey } from '../actions/schema';

const initialState: UnifiedLeasePricingStateModel = {
  isLoading: false,
  lastUpdated: '',
  priceRecommendationModal: {
    isOpen: false,
    termOffers: undefined,
    propertyCode: undefined,
    unitType: undefined,
    unitTypeCode: undefined,
  },
  priceMatricesModal: {
    matrices: new Map(),
    isOpen: false,
    unitTypeKey: undefined,
    propertyCode: undefined,
    unitType: undefined,
    unitCode: undefined,
    beds: undefined,
    name: undefined,
    sqft: undefined,
    isShiftOffersOpen: false,
  },
  flagItemModal: {
    isOpen: false,
    currentItem: undefined,
    isLoadingComments: false,
  },
  filtersOptions: {
    beds: undefined,
    unitTypes: undefined,
    propertyCodes: undefined,
    sortBy: [
      { value: 'UnitType_Code-desc', text: 'No Sort' },
      { value: 'Beds-desc', text: 'Bedroom: High to Low' },
      { value: 'Beds-asc', text: 'Bedroom: Low to High' },
      { value: 'availableUnits-desc', text: 'Available: High to Low' },
      { value: 'availableUnits-asc', text: 'Available: Low to High' },
      { value: 'occupiedUnits-desc', text: 'Occupied: High to Low' },
      { value: 'occupiedUnits-asc', text: 'Occupied: Low to High' },
    ],
    units: undefined,
    statsPeriod: [
      { id: 3, name: 'Last 3 Months' },
      { id: 4, name: 'Last 4 Months' },
      { id: 6, name: 'Last 6 Months' },
      { id: 12, name: 'Last 12 months' },
    ],
  },
  selectedFilters: {
    offerStatus: OFFER_STATES.IN_REVIEW,
    unitStatus: 'Exposed',
    propertyCode: { id: ALL, name: ALL },
    unitType: ALL,
    beds: ALL,
    statsPeriod: 6,
  },
  paging: {
    limit: 10,
    page: 0,
    sortBy: 'UnitType_Code-desc',
    allPages: 1,
  },
  stats: undefined,
  floorplanData: undefined,
  unitsData: {},
  approveAllModalIsOpen: false,
};

const getOptions = (properties: any, unitTypes: any) => {
  const propertyOptions = [
    {
      text: ALL,
      value: ALL,
    },
  ];

  const unitTypeOptions = [
    {
      text: ALL,
      value: ALL,
    },
  ];

  const bedOptions = [
    {
      text: ALL,
      value: ALL,
    },
  ];

  const propertyCodes = new Set<string>();
  const unitTypeSet = new Set<string>();
  const beds = new Set<string>();

  Object.values(properties).forEach((property: any) => {
    if (!propertyCodes.has(property.PropertyCode)) {
      propertyOptions.push({
        text: property.PropertyName,
        value: property.PropertyCode,
      });
      propertyCodes.add(property.PropertyCode);
    }

    if (property.BedRooms && Array.isArray(property.BedRooms)) {
      property.BedRooms.forEach((bed: number) => {
        if (!beds.has(String(bed))) {
          bedOptions.push({
            text: String(bed),
            value: String(bed),
          });
          beds.add(String(bed));
        }
      });
    }
  });

  Object.values(unitTypes).forEach((propertyUnitTypes: any) => {
    if (
      propertyUnitTypes.unit_types &&
      Array.isArray(propertyUnitTypes.unit_types)
    ) {
      propertyUnitTypes.unit_types.forEach((unitType: any) => {
        if (!unitTypeSet.has(unitType.Unit_Type)) {
          unitTypeOptions.push({
            text: `${unitType.Name} (${unitType.Unit_Type})`,
            value: unitType.Unit_Type,
          });
          unitTypeSet.add(unitType.Unit_Type);
        }
      });
    }
  });

  return {
    propertyOptions,
    unitTypeOptions,
    bedOptions,
  };
};

const unifiedLeasePricingReducer = (
  state = initialState,
  action: AnyAction
) => {
  switch (action.type) {
    case UNITS_ACTIONS.FETCH_DATA: {
      return produce(state, (df) => {
        df.unitsData[action.payload.id] = action.payload.data;
      });
    }
    case FLOORPLAN_ACTIONS.FETCH_DATA: {
      return produce(state, (df) => {
        df.paging = action.payload.paging;
        df.floorplanData = action.payload.data;
        df.lastUpdated = action.payload.lastUpdated;
      });
    }

    case FILTERS_ACTIONS.SET_FILTERS_OPTIONS: {
      const options = getOptions(
        action.payload.properties,
        action.payload.unitTypes
      );

      return produce(state, (df) => {
        // if (!state.filtersOptions.propertyCodes)
        df.filtersOptions.propertyCodes = options.propertyOptions;
        // if (!state.filtersOptions.unitTypes)
        df.filtersOptions.unitTypes = options.unitTypeOptions;
        // if (!state.filtersOptions.beds)
        df.filtersOptions.beds = options.bedOptions;
      });
    }

    case FLOORPLAN_ACTIONS.SET_CURRENT_PAGE: {
      return produce(state, (df) => {
        if (df.paging) df.paging.page = action.payload;
      });
    }

    case FLOORPLAN_ACTIONS.SET_ELEMENTS_PER_PAGE: {
      return produce(state, (df) => {
        if (df.paging) {
          df.paging.limit = action.payload;
          df.paging.page = 0;
        }
      });
    }

    case FLOORPLAN_ACTIONS.APPROVE_OFFER: {
      return produce(state, (df) => {
        if (df.floorplanData && state.floorplanData) {
          const newFloorplanData = state.floorplanData.filter(
            (item) =>
              item.propertyCode !== action.payload.propertyCode ||
              item.unitType !== action.payload.unitType
          );

          df.floorplanData = newFloorplanData;
          if (df.paging && newFloorplanData.length === 0) {
            df.paging.page = df.paging.page - 1;
          }
        }
      });
    }

    case FLOORPLAN_ACTIONS.EXCLUDE_OFFER: {
      return produce(state, (df) => {
        if (df.floorplanData && state.floorplanData) {
          const index = df.floorplanData.findIndex(
            (item) =>
              item.propertyCode === action.payload.propertyCode &&
              item.unitType === action.payload.unitType
          );

          if (index !== -1) {
            df.floorplanData[index].isApproved =
              !state.floorplanData[index].isApproved;
            df.floorplanData[index].isExcluded =
              !state.floorplanData[index].isExcluded;
          }

          const id = `${action.payload.propertyCode}~${action.payload.unitType}`;
          if (df.unitsData[id]) {
            const newUnits = df.unitsData[id].map((item) => {
              item.isApproved = !item.isApproved;
              item.isExcluded = !item.isExcluded;
              return item;
            });

            df.unitsData[id] = newUnits;
          }
        }
      });
    }

    case FLOORPLAN_ACTIONS.RESET_OFFER: {
      return produce(state, (df) => {
        if (df.floorplanData && state.floorplanData) {
          let newOffers = df.floorplanData.map((item) => {
            if (item.code === action.payload) {
              item.isApproved = false;
              item.isExcluded = false;
            }
            return item;
          });

          df.floorplanData = newOffers;
        }
      });
    }

    case FILTERS_ACTIONS.SET_STATS_PERIOD: {
      return produce(state, (df) => {
        if (df.selectedFilters) df.selectedFilters.statsPeriod = action.payload;
      });
    }

    case FILTERS_ACTIONS.SET_BED: {
      return produce(state, (df) => {
        if (df.selectedFilters) df.selectedFilters.beds = action.payload;
      });
    }

    case FILTERS_ACTIONS.SET_PROPERTY_CODE: {
      return produce(state, (df) => {
        if (df.selectedFilters)
          df.selectedFilters.propertyCode = action.payload;
      });
    }

    case FILTERS_ACTIONS.SET_UNIT_TYPE: {
      return produce(state, (df) => {
        if (df.selectedFilters) df.selectedFilters.unitType = action.payload;
      });
    }

    case FILTERS_ACTIONS.SET_UNIT_STATE: {
      return produce(state, (df) => {
        df.selectedFilters.unitStatus = action.payload;
      });
    }

    case FILTERS_ACTIONS.SET_SORT_BY: {
      return produce(state, (df) => {
        if (df.paging) df.paging.sortBy = action.payload;
      });
    }

    case FILTERS_ACTIONS.SET_OFFER_STATE: {
      return produce(state, (df) => {
        if (df.selectedFilters) df.selectedFilters.offerStatus = action.payload;
      });
    }

    case STATS_ACTIONS.FETCH_DATA: {
      return produce(state, (df) => {
        df.stats = action.payload.data;
      });
    }

    case PRICE_MATRICE_MODAL_ACTIONS.FETCH_DATA: {
      return produce(state, (df) => {
        df.priceMatricesModal.matrices.set(
          action.payload.unitTypeKey,
          action.payload.data
        );
        df.priceMatricesModal.unitType = action.payload.unitType;
        df.priceMatricesModal.propertyCode = action.payload.propertyCode;
        df.priceMatricesModal.unitTypeKey = action.payload.unitTypeKey;
        df.priceMatricesModal.unitDetailId = action.payload.unitDetailId;
        df.priceMatricesModal.beds = action.payload.beds;
        df.priceMatricesModal.name = action.payload.name;
        df.priceMatricesModal.sqft = action.payload.sqft;
        df.priceMatricesModal.unitCode = action.payload.unitCode;
        df.priceMatricesModal.isOpen = true;
        df.priceMatricesModal.isShiftOffersOpen =
          action.payload.isShiftOffersOpen;
      });
    }

    case PRICE_MATRICE_MODAL_ACTIONS.SET_DATA: {
      return produce(state, (df) => {
        df.priceMatricesModal.unitType = action.payload.unitType;
        df.priceMatricesModal.propertyCode = action.payload.propertyCode;
        df.priceMatricesModal.unitTypeKey = action.payload.unitTypeKey;
        df.priceMatricesModal.unitDetailId = action.payload.unitDetailId;
        df.priceMatricesModal.beds = action.payload.beds;
        df.priceMatricesModal.name = action.payload.name;
        df.priceMatricesModal.sqft = action.payload.sqft;
        df.priceMatricesModal.unitCode = action.payload.unitCode;
        df.priceMatricesModal.isOpen = true;
        df.priceMatricesModal.isShiftOffersOpen =
          action.payload.isShiftOffersOpen;
      });
    }

    case PRICE_MATRICE_MODAL_ACTIONS.CLEAR_DATA: {
      return produce(state, (df) => {
        df.priceMatricesModal.unitType = undefined;
        df.priceMatricesModal.propertyCode = undefined;
        df.priceMatricesModal.unitTypeKey = undefined;
        df.priceMatricesModal.unitDetailId = undefined;
        df.priceMatricesModal.beds = undefined;
        df.priceMatricesModal.name = undefined;
        df.priceMatricesModal.sqft = undefined;
        df.priceMatricesModal.unitCode = undefined;
        df.priceMatricesModal.isOpen = false;
        df.priceMatricesModal.isShiftOffersOpen = false;
      });
    }

    case PRICE_MATRICE_MODAL_ACTIONS.SET_SHIFT_OFFERS_MODAL: {
      return produce(state, (df) => {
        df.priceMatricesModal.isShiftOffersOpen = action.payload;
      });
    }

    case PRICE_MATRICE_MODAL_ACTIONS.SHIFT_ALL_OFFERS: {
      return produce(state, (df) => {
        const key = `${action.payload.propertyCode}~${action.payload.unitType}`;

        if (action.payload.unitId && df.unitsData[key]) {
          const index = df.unitsData[key].findIndex(
            (item) => item.unit === action.payload.unitId
          );

          if (index !== -1) {
            df.unitsData[key][index].bestOffer +=
              action.payload.shiftAmount *
              (action.payload.shiftBy === 'perc'
                ? 0.01 * df.unitsData[key][index].bestOffer
                : 1);
          }
        } else {
          const index = df.floorplanData?.findIndex((item) => {
            return (
              item.propertyCode === action.payload.propertyCode &&
              item.unitType === action.payload.unitType
            );
          });

          if (index !== -1) {
            df.floorplanData[index].bestOffer +=
              action.payload.shiftAmount *
              (action.payload.shiftBy === 'perc'
                ? 0.01 * df.floorplanData?.[index].bestOffer
                : 1);

            df.unitsData[key].forEach((item: any) => {
              item.bestOffer +=
                action.payload.shiftAmount *
                (action.payload.shiftBy === 'perc' ? 0.01 * item.bestOffer : 1);
            });

            df.priceMatricesModal.matrices.forEach((value, matrixKey) => {
              const leaseKey = generateLeaseKey(
                action.payload.propertyCode,
                action.payload.unitType
              );
              if (matrixKey.startsWith(`${leaseKey}-`)) {
                df.priceMatricesModal.matrices.delete(matrixKey);
              }
            });
          }
        }
      });
    }

    case PRICE_RECOMMENDATION_MODAL_ACTIONS.SET_DATA: {
      return produce(state, (df) => {
        df.priceRecommendationModal.isOpen = true;
        df.priceRecommendationModal.termOffers = action.payload.termOffers;
        df.priceRecommendationModal.propertyCode = action.payload.propertyCode;
        df.priceRecommendationModal.unitType = action.payload.unitType;
        df.priceRecommendationModal.unitTypeCode = action.payload.unitTypeCode;
      });
    }

    case PRICE_RECOMMENDATION_MODAL_ACTIONS.CLEAR_DATA: {
      return produce(state, (df) => {
        df.priceRecommendationModal.isOpen = false;
        df.priceRecommendationModal.termOffers = undefined;
        df.priceRecommendationModal.propertyCode = undefined;
        df.priceRecommendationModal.unitType = undefined;
        df.priceRecommendationModal.unitTypeCode = undefined;
      });
    }

    case PRICE_RECOMMENDATION_MODAL_ACTIONS.UPDATE_PRICE: {
      return produce(state, (df) => {
        if (
          df.priceRecommendationModal.termOffers &&
          df.unitsData &&
          df.floorplanData
        ) {
          df.priceRecommendationModal.termOffers =
            df.priceRecommendationModal.termOffers.map((item: any) => {
              return item;
            });

          df.unitsData[action.payload.propertyCode] = df.unitsData[
            action.payload.propertyCode
          ].map((item) => {
            return item;
          });

          df.floorplanData = df.floorplanData.map((item) => {
            return item;
          });
        }
      });
    }

    case MODULE_ACTIONS.IS_LOADING: {
      return produce(state, (df) => {
        df.isLoading = action.payload;
      });
    }

    case FLAG_ITEM_MODAL_ACTIONS.SET_ITEM: {
      return produce(state, (df) => {
        df.flagItemModal.isOpen = true;
        df.flagItemModal.currentItem = action.payload;
      });
    }

    case FLAG_ITEM_MODAL_ACTIONS.CLEAR_ITEM: {
      return produce(state, (df) => {
        df.flagItemModal.isOpen = false;
        df.flagItemModal.currentItem = undefined;
      });
    }

    case FLAG_ITEM_MODAL_ACTIONS.RESOLVE_FLAG: {
      return produce(state, (df) => {
        if (df.flagItemModal.currentItem) {
          df.flagItemModal.currentItem.resolved = true;
        }

        const newFloorplanData = state.floorplanData?.filter(
          (item) =>
            item.propertyCode !==
              action.payload.flag.FlagDetails.propertyCode ||
            item.unitType !== action.payload.flag.FlagDetails.unitType
        );

        df.floorplanData = df.floorplanData?.map((item) => {
          if (
            item.propertyCode ===
              action.payload.flag.FlagDetails.propertyCode &&
            item.unitType === action.payload.flag.FlagDetails.unitType
          ) {
            item.isFlagged = true;
          }
          return item;
        });

        df.floorplanData = newFloorplanData;
        df.flagItemModal.isOpen = false;
      });
    }

    case FLAG_ITEM_MODAL_ACTIONS.ADD_COMMENT: {
      return produce(state, (df) => {
        if (df.flagItemModal.currentItem) {
          df.flagItemModal.currentItem.comments = state.flagItemModal
            .currentItem?.comments
            ? [
                ...state.flagItemModal.currentItem.comments,
                action.payload.comment,
              ]
            : [action.payload.comment];
          df.flagItemModal.currentItem.id = action.payload.flag.id;
        }

        df.floorplanData = df.floorplanData?.filter(
          (item) =>
            item.propertyCode !==
              action.payload.flag.FlagDetails.propertyCode ||
            item.unitType !== action.payload.flag.FlagDetails.unitType
        );
      });
    }

    case FLOORPLAN_ACTIONS.SET_SHOW_APPROVE_ALL_CONFIRMATION: {
      return produce(state, (df) => {
        df.approveAllModalIsOpen = action.payload;
      });
    }
    case AUTH_ACTIONS.LOGOUT: {
      return initialState;
    }
    default: {
      return state;
    }
  }
};

export default unifiedLeasePricingReducer;
